import { useEffect } from "react";
import { Outlet } from "react-router-dom";
import { useRestaurantStore } from "@/stores/restaurantStore";
import { usePosIdentityStore } from "@/stores/posIdentityStore";
import {
  useStaffPinsConfigured,
  useVerifyStaffPin,
} from "@/rc/hooks/staff/use-employee-pins";
import { PinPad } from "./PinPad";
import { Spinner } from "@/components/ui/spinner";
import { isAdmin } from "@/lib/auth/roles";

const EXPIRY_POLL_MS = 10_000;

/**
 * Shared-device identification: owner/admin skip; non-admin staff verify PIN when any staff PIN exists.
 * Used as a layout route — renders <Outlet /> when the gate passes.
 */
export function PinGate() {
  const activeRole = useRestaurantStore((s) => s.activeRole);
  const activeRestaurantId = useRestaurantStore((s) => s.activeRestaurantId);
  const identityValid = usePosIdentityStore((s) => s.isValid());

  // Owner/admin always bypass — checked before the pins query to avoid unnecessary fetch.
  const adminBypass = isAdmin(activeRole);

  const { data: pinsConfigured, isLoading: pinsLoading } =
    useStaffPinsConfigured(adminBypass ? undefined : activeRestaurantId);

  const { verify, verifying, error } = useVerifyStaffPin(activeRestaurantId);

  // Clear expired identity on a regular interval.
  useEffect(() => {
    const id = window.setInterval(() => {
      const store = usePosIdentityStore.getState();
      if (store.profileId && !store.isValid()) {
        store.clear();
      }
    }, EXPIRY_POLL_MS);
    return () => window.clearInterval(id);
  }, []);

  // Still resolving role / restaurant — wait.
  if (activeRole === null || !activeRestaurantId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Spinner className="text-muted-foreground" />
      </div>
    );
  }

  // Admin roles skip the PIN gate entirely.
  if (adminBypass) {
    return <Outlet />;
  }

  // Waiting for the pins-configured check.
  if (pinsLoading || pinsConfigured === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Spinner className="text-muted-foreground" />
      </div>
    );
  }

  // No staff PINs configured — nothing to gate.
  if (!pinsConfigured) {
    return <Outlet />;
  }

  // Valid identity — allow through.
  if (identityValid) {
    return <Outlet />;
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4 gap-4">
      <PinPad onSubmit={verify} disabled={verifying} errorMessage={error} />
    </div>
  );
}
